import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Machine, ValidationResult } from '@couch/shared';
import { useState } from 'react';
import { api, errorMessage } from '../../api/client';
import { openComfy } from '../machines/open-comfy';
import { runsKey } from './RunsPanel';

interface Props { recipeId: string; version: number | undefined; machine: Machine | undefined; inputs?: Record<string, string> }

function ValidationReport({ result }: { result: ValidationResult }) {
  if (result.valid && result.warnings.length === 0) return <p className="success" role="status">Everything fits this machine.</p>;
  const items = [...result.errors.map((i) => ({ ...i, kind: 'Error' })), ...result.warnings.map((i) => ({ ...i, kind: 'Note' }))];
  return (
    <ul className="validation" aria-label="Check result">
      {items.map((i, n) => (
        <li key={n} className={i.kind === 'Error' ? 'form-error' : 'muted'}>
          <strong>{i.kind}</strong>{i.nodeId && ` · node ${i.nodeId}${i.input ? ` · ${i.input}` : ''}`}: {i.message}
        </li>
      ))}
    </ul>
  );
}

/** Check, run and open the recipe on the chosen machine. */
export function RecipeActions({ recipeId, version, machine, inputs = {} }: Props) {
  const queryClient = useQueryClient();
  const [openError, setOpenError] = useState('');
  const body = { ...(machine ? { machineId: machine.id } : {}), ...(version ? { version } : {}) };
  const check = useMutation({ mutationFn: () => api.validateRecipe(recipeId, body) });
  const run = useMutation({
    mutationFn: (randomizeSeed: boolean) => {
      // Chosen input pictures go in as per-run overrides of LoadImage (doc 33).
      const entries = Object.entries(inputs);
      const overrides = entries.length ? Object.fromEntries(entries.map(([id, image]) => [id, { image }])) : undefined;
      return api.startRun(recipeId, { ...body, randomizeSeed, ...(overrides ? { overrides } : {}) });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: runsKey(recipeId) }),
  });
  const offline = !machine?.online;
  return (
    <div className="panel">
      <div className="machine-actions">
        <button type="button" disabled={offline || run.isPending} onClick={() => { run.mutate(false); }}>Run</button>
        <button type="button" className="button-quiet" disabled={offline || run.isPending} onClick={() => { run.mutate(true); }}>Run with a new seed</button>
        <button type="button" className="button-quiet" disabled={offline || check.isPending} onClick={() => { check.mutate(); }}>Check</button>
        <button type="button" className="button-quiet" disabled={offline} onClick={() => {
          if (machine) openComfy(machine.id, setOpenError, { recipeId, ...(version ? { version } : {}) });
        }}>Open in ComfyUI</button>
      </div>
      {offline && <p className="muted">{machine ? `${machine.name} is offline.` : 'Choose a machine to run this workflow on.'}</p>}
      {check.data && <ValidationReport result={check.data} />}
      {[check.error, run.error].filter(Boolean).map((e, n) => (
        <div key={n} role="alert" className="form-error">
          <p>{errorMessage(e)}</p>
          {e instanceof Error && 'details' in e && Array.isArray(e.details) && (
            <ul>{(e.details as { path: string; message: string }[]).map((d) => <li key={d.path + d.message}>{d.path}: {d.message}</li>)}</ul>
          )}
        </div>
      ))}
      {openError && <p role="alert" className="form-error">{openError}</p>}
    </div>
  );
}
